import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Layout/Header';
import { monitorApi } from '../services/api';
import { formatDate, formatDuration } from '../config/appConfig';
import { MonitorEvent } from '../types';

const IncidentsPage: React.FC = () => {
  const [events, setEvents] = useState<MonitorEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'ongoing' | 'resolved'>('all');

  useEffect(() => {
    monitorApi.getRecentEvents(200)
      .then(res => setEvents(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = events.filter(e =>
    filter === 'all' ? true : filter === 'ongoing' ? !e.resolved_at : !!e.resolved_at
  );

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Incidents</h1>
            <p className="text-sm text-slate-500 mt-1">All recorded downtime events across monitors.</p>
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {(['all', 'ongoing', 'resolved'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md capitalize ${
                  filter === f ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="px-5 py-16 text-center text-slate-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-5 py-16 text-center text-slate-400">No incidents to show.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                  <th className="px-5 py-3 font-medium">Monitor</th>
                  <th className="px-5 py-3 font-medium">Root Cause</th>
                  <th className="px-5 py-3 font-medium">Code</th>
                  <th className="px-5 py-3 font-medium">Started</th>
                  <th className="px-5 py-3 font-medium">Duration</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(e => (
                  <tr key={e.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <Link to={`/monitor/${e.monitor_id}`} className="font-medium text-brand hover:underline">
                        {e.monitor_name}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{e.root_cause}</td>
                    <td className="px-5 py-3 text-slate-600">{e.status_code ?? '—'}</td>
                    <td className="px-5 py-3 text-slate-500">{formatDate(e.started_at)}</td>
                    <td className="px-5 py-3 text-slate-600">{formatDuration(e.duration_seconds)}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        e.resolved_at ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {e.resolved_at ? 'Resolved' : 'Ongoing'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncidentsPage;
